/**
 * 신규 점포 개설 절차 — 단계 데이터 (v3: 9단계 + 추가 인허가 7종 + 상세 모달 데이터)
 * 권위 문서: docs/console/2026-05-14_corporate-store-opening-procedure.md §1, §4
 */

export type StepId =
  | 'P2' | 'P3' | 'P1' | 'A' | 'B' | 'C' | 'D' | 'E' | 'E2' | 'F'
  | '4.1' | '4.2' | '4.3' | '4.4' | '4.5' | '4.6' | '4.7'
  | 'PRE-PG' | 'PRE-FIRE';

/** v1 호환 (StoreOpeningPage 기존 코드 참조용) */
export type StoreType = 'corporate-direct' | 'franchise';

export type DocumentRow = {
  name: string;
  required: '필수' | '조건부' | '필수(법인)';
  condition?: string;
};

export type ReferenceLink = {
  label: string;
  url: string;
};

export type PrerequisiteRow = {
  what: string;
  detail: string;
};

export type StepDetail = {
  agency: string;
  applicationMethod?: string;
  processingTime?: string;
  fee?: string;
  documents?: DocumentRow[];
  prerequisites?: PrerequisiteRow[];
  references?: ReferenceLink[];
  notes?: string[];
};

export interface Step {
  id: StepId;
  label: string;       // 카드에 표시할 단계 번호 (P-2, A, 4.1 …)
  name: string;
  org: string;
  duration: string;
  docs: string[];
  note?: string;
  category: 'core' | 'addon';  // core = 9단계 필수, addon = 추가 인허가
  description?: string;        // 한 줄 상세 안내
  detail?: StepDetail;         // 클릭 모달 상세 데이터 (없으면 클릭 무반응)
  /** @deprecated v1 호환. stepActivation.ts 의 isStepActive() 사용 권장 */
  franchiseOnly: boolean;
  /** 표시용 단계 번호 (PRE-PG, 방염 등 addon 전용) */
  number?: string;
  /** 선행 단계 ID 문자열 (표시용) */
  predecessor?: string;
}

/** 9단계 필수 (core) */
const CORE_STEPS: Step[] = [
  {
    id: 'P2',
    label: 'P-2',
    name: '정보공개서 수령',
    org: '가맹본부',
    duration: '14일 숙고',
    docs: ['본부의 공정위 등록 정보공개서'],
    note: '가맹계약 체결 전 필수. 수령일로부터 14일 (변호사·가맹거래사 자문 시 7일) 후에만 계약 가능.',
    category: 'core',
    description: '프랜차이즈 가맹점만 해당. 법인 직영·개인은 건너뜀.',
    franchiseOnly: true,
  },
  {
    id: 'P3',
    label: 'P-3',
    name: '가맹계약 체결',
    org: '가맹본부',
    duration: 'P-2 후 진행',
    docs: ['정보공개서 수령일 +14일 경과 확인서'],
    note: '가맹금 수령·계약 체결 모두 14일 이전 금지.',
    category: 'core',
    description: '프랜차이즈 가맹점만 해당.',
    franchiseOnly: true,
  },
  {
    id: 'P1',
    label: 'P-1',
    name: '임대차계약',
    org: '건물주/임대인',
    duration: '당일~수일',
    docs: ['신분증 (또는 법인 서류)', '인감도장'],
    category: 'core',
    description: '모든 형태의 사업자에 필수.',
    franchiseOnly: false,
  },
  {
    id: 'A',
    label: 'A',
    name: '보건증 발급',
    org: '보건소',
    duration: '약 5일',
    docs: ['신분증', '수수료 (약 3,000원)'],
    note: '혈액검사 결과 대기. 전체 일정의 critical path — 가장 먼저 착수.',
    category: 'core',
    description: '조리·서빙에 종사하는 직원 전원 필수. 5일 소요라 가장 먼저 시작.',
    franchiseOnly: false,
  },
  {
    id: 'B',
    label: 'B',
    name: '식품위생교육 이수',
    org: '한국외식업중앙회 등',
    duration: '6시간 (기관 스케줄)',
    docs: ['신분증', '수수료'],
    note: '신규 6시간. 조리사·영양사·위생사 면허 소지자는 면제.',
    category: 'core',
    description: '면허 소지자가 아닌 영업자는 신규 6시간 이수 필수.',
    franchiseOnly: false,
    detail: {
      agency: '한국외식업중앙회 / 한국외식산업협회 (일반음식점) · 한국휴게음식업중앙회 (휴게음식점) · 대한제과협회 (제과점)',
      applicationMethod: '온라인(ifoodedu.or.kr) 또는 방문 (전국 16개 교육원, 매주 집합교육 일정 사전 확인)',
      processingTime: '온라인 수강 = 수료 즉시 이수증 출력 / 방문 = 당일',
      fee: '26,000원 (신규영업자 6시간 과정)',
      documents: [
        { name: '교육신청서', required: '필수', condition: '교육원 현장 작성 또는 온라인 입력' },
        { name: '신분증 (주민등록증·운전면허증 등)', required: '필수', condition: '본인 확인용' },
        { name: '수강료 납부', required: '필수', condition: '현장 결제 또는 온라인 사전 결제' },
        {
          name: '위생관리책임자 지정서 (별지 1호)',
          required: '조건부',
          condition: '영업자 미상주 또는 2개소 이상 운영 시 — 교육기관 제출 (영업신고증 첨부 아님)',
        },
      ],
      references: [
        { label: '한국외식업중앙회 식품위생교육', url: 'https://www.ifoodedu.or.kr/' },
        { label: '식품위생법 §41', url: 'https://www.law.go.kr/LSW/lsLinkCommonInfo.do?lsJoLnkSeq=1018920785' },
      ],
      notes: [
        '영업신고 전에 이수해야 함. 영업 시작 후 이수는 과태료 100만원 이하 대상',
        '영업자(대표)가 매장에 직접 종사 안 하거나 2개소 이상 운영 시 종업원 1명을 위생관리책임자로 지정해 대리 수강 가능',
        '조리사·영양사·위생사 면허자는 신규 교육 면제',
        '**위생관리책임자 지정서**: 영업자가 매장에 직접 종사 안 함 또는 2개소 이상 운영 시 종업원 1명을 위생관리책임자로 지정. 「식품관련영업자등에대한위생교육지침」 별지 1호 서식. **영업신고증 첨부서류 아님** — 교육기관(외식업중앙회 등)에 제출하고 교육기관이 보관. 위생관리책임자는 매년 보수교육 3시간 추가 이수 의무.',
      ],
    },
  },
  {
    id: 'C',
    label: 'C',
    name: '소방완비증명서',
    org: '소방서',
    duration: '약 3일 (현장검사)',
    docs: ['소방시설 설계도서', '완공신고서', '설치내역서'],
    note: '다중이용업소 해당 시 의무. 지하 66㎡ 이상 또는 1층 직결 외 100㎡ 이상.',
    category: 'core',
    description: '공간 조건 3가지(지하층/1층직결/면적)로 자동 판정됩니다.',
    franchiseOnly: false,
    detail: {
      agency: '관할 소방서',
      applicationMethod: '소방시설공사업 등록 업체 선정·계약 → 인테리어 공사 중 소방시설 설치 → 안전시설등 설치신고(처리 3일) → 완공신고(처리 3일) → 소방관 현장검사 → 완비증명서 발급',
      processingTime: '공사 포함 2~6주 (공사 규모에 따라 편차 큼)',
      fee: '소방공사비 (업체별 상이) + 방염처리비 (해당 시)',
      documents: [
        { name: '소방시설 설계도서', required: '필수' },
        { name: '안전시설등 설치신고서', required: '필수' },
        { name: '완공신고서', required: '필수' },
        { name: '설치내역서', required: '필수' },
        { name: '방염성능시험 합격증', required: '조건부', condition: '목재·커튼·카펫 등 방염 대상물품 있는 경우' },
      ],
      prerequisites: [
        {
          what: '방염처리',
          detail: '목재·커튼·카펫 있을 때. 방염업체 1~2주. 방염성능시험 합격증이 완공신고 첨부서류에 포함됨. 아래 "방염처리(PRE-FIRE)" 단계 참조.',
        },
        {
          what: '소방공사 완료',
          detail: '소방시설공사업 등록 업체에 의한 공사 완료 + 완공신고서 발급.',
        },
      ],
      references: [
        { label: '소방시설 설치 및 관리에 관한 법률 §20', url: 'https://www.law.go.kr/lsInfoP.do?lsId=015456' },
        { label: '다중이용업소법 시행령 §2', url: 'https://www.law.go.kr/lumLsLinkPop.do?lspttninfSeq=109754' },
      ],
      notes: [
        '소방필증(안전시설등 완비증명서)은 영업신고 전 가장 오래 걸리는 서류 — 면적 기준 먼저 확인하고 가장 먼저 착수',
        '지하 66㎡ 이상 또는 지상(1층 외부직결 아닌) 100㎡ 이상 시 의무 대상',
        '방염 처리 안 한 자재 사용 시 소방완비증명 발급 불가',
      ],
    },
  },
  {
    id: 'D',
    label: 'D',
    name: '영업신고증',
    org: '구청 위생과',
    duration: '3~5일',
    docs: ['B 이수증', 'C 완비증명(해당시)', 'P-1 임대차계약서'],
    category: 'core',
    description: '핵심 허가. 사업자등록(E)보다 먼저 완료해야 합니다.',
    franchiseOnly: false,
    detail: {
      agency: '관할 시·군·구청 위생과 (또는 보건소)',
      applicationMethod: '방문 / 정부24 (https://www.gov.kr) / 우편',
      processingTime: '3일 (업무일 기준)',
      fee: '신고 수수료 28,000원 + 등록면허세 27,000~67,500원 (영업장 면적별 종별 차등)',
      documents: [
        { name: '식품영업신고서 (별지 제37호서식)', required: '필수', condition: '구청·보건소 비치 또는 정부24 출력' },
        { name: '식품위생교육 이수증', required: '필수', condition: '영업 전 6시간 신규교육 이수 후 발급받은 것' },
        { name: '건강진단결과서 (보건증)', required: '필수', condition: '식품위생법 §49 대상자(조리·판매 종사자) — 보건소 또는 지정 의료기관 발급' },
        { name: '건물 임대차계약서', required: '필수', condition: '타인 건물 사용 시. 직접 소유 시 등기부등본으로 대체' },
        { name: '안전시설등 완비증명서', required: '조건부', condition: '다중이용업소법 대상(지하 66㎡↑ 또는 지상 100㎡↑) — 소방서 발급' },
        { name: '액화석유가스 사용시설완성검사증명서', required: '조건부', condition: 'LPG 사용 시 — 한국가스안전공사 발급' },
        { name: '수질검사성적서', required: '조건부', condition: '지하수 사용 시 — 먹는물수질검사기관' },
        { name: '건물주 동의서 또는 신탁동의서', required: '조건부', condition: '전대차(재임대) 또는 신탁 건물 사용 시' },
        { name: '어린이놀이시설 설치검사합격증', required: '조건부', condition: '어린이놀이시설 설치 시' },
        { name: '복어 조리사 면허증 사본', required: '조건부', condition: '복어 요리 취급 시' },
        { name: '공유주방 사용계약서', required: '조건부', condition: '공유주방 이용 영업자' },
        { name: '법인등기부등본', required: '필수(법인)', condition: '법인 사업자' },
      ],
      references: [
        { label: '정부24 식품관련영업신고', url: 'https://www.gov.kr/mw/AA020InfoCappView.do?HighCtgCD=A09006&CappBizCD=14600000021&tp_seq=02' },
        { label: '식품위생법 시행규칙 별지 제37호서식', url: 'https://www.law.go.kr/LSW/lsLawLinkInfo.do?lsJoLnkSeq=1000607909' },
        { label: '찾기쉬운생활법령 음식점 창업', url: 'https://easylaw.go.kr/CSP/CnpClsMain.laf?popMenu=ov&csmSeq=839&ccfNo=5&cciNo=1&cnpClsNo=1' },
      ],
      prerequisites: [
        {
          what: 'LPG 완성검사증명서',
          detail: '한국가스안전공사. LPG 시설 공사 완료 후 사용 시작 7일 전까지 신청 의무. 영업신고 일정 역산 필수.',
        },
      ],
      notes: [
        '안전시설완비증명서(소방필증)는 소방서 현장 답사 후 3일 내 발급 — 영업신고 전 가장 오래 걸리는 서류. 면적 기준 먼저 확인',
        '위생교육 이수증은 영업신고 전에 이수한 경우만 유효. 영업 시작 후 이수는 과태료 대상',
      ],
    },
  },
  {
    id: 'E',
    label: 'E',
    name: '사업자등록증',
    org: '세무서 (홈택스)',
    duration: '약 2일',
    docs: ['D 영업신고증 사본', 'P-1 임대차계약서'],
    note: '법인 직영: 법인등기부등본·이사회의사록·종된 사업장 명세서 추가.',
    category: 'core',
    description: 'D 영업신고 후 신청. 법인 직영은 추가 서류 필요.',
    franchiseOnly: false,
  },
  {
    id: 'E2',
    label: 'E-2',
    name: '상법상 지점 설치 등기',
    org: '관할 등기소 (법무사 위임 가능)',
    duration: '본점 2주 / 지점 3주 이내',
    docs: ['지점 설치 등기 신청서', '이사회의사록 (공증)', '등록면허세 영수필확인서'],
    note: '2호점 이상 법인 직영 시 상법 §181 의무. 사업자등록 방식과 무관.',
    category: 'core',
    description: '법인 직영 2호점 이상 — 상법 §181에 따라 지점 설치 결의일로부터 본점 2주·지점 3주 이내 등기 의무.',
    franchiseOnly: false,
    detail: {
      agency: '관할 등기소 (직접 신청 또는 법무사 위임 가능)',
      applicationMethod: '등기소 방문 또는 전자등기 (인터넷등기소 iros.go.kr) — 법무사 위임 가능',
      processingTime: '본점 소재지 2주 / 지점 소재지 3주 이내 신청 (지점 설치 결의일 기산)',
      fee: '등록면허세 40,200원 + 지방교육세 20% (8,040원) — 수도권 과밀억제권역 3배 중과 시 120,600원. 법무사 수수료 별도 (사무소별 상이)',
      documents: [
        { name: '지점 설치 등기 신청서', required: '필수' },
        {
          name: '이사회의사록 (공증)',
          required: '필수',
          condition: '소규모 회사(자본금 10억 미만+이사 2인 이하)는 대표이사 결정서로 대체 가능 (출처 미확인)',
        },
        { name: '등록면허세 영수필 확인서', required: '필수' },
        { name: '등기신청수수료 영수필 확인서', required: '필수' },
        { name: '법인등기부등본', required: '필수' },
        { name: '정관 사본', required: '조건부', condition: '지점 소재지 등기소 제출 시 요구될 수 있음' },
        { name: '위임장', required: '조건부', condition: '법무사 위임 시' },
      ],
      references: [
        { label: '상법 §181 (지점 설치의 등기)', url: 'https://www.law.go.kr/LSW/lsLawLinkInfo.do?chrClsCd=010202&lsJoLnkSeq=1000734237' },
        { label: '인터넷등기소', url: 'https://www.iros.go.kr/' },
      ],
      prerequisites: [
        {
          what: '이사회 결의 + 공증',
          detail: '이사 3인 이상 법인은 공증 필수. 이사 2인 이하·자본금 10억 미만은 대표이사 결정서로 대체 가능 [출처 미확인, 법무사 확인 권고]. 공증 소요 1~2일.',
        },
      ],
      notes: [
        '2호점부터 상법상 자동 의무 — 사업자등록 방식(별도/사업자단위과세)과 무관하게 결합 가능',
        '**사업자단위과세 (종된 사업장) 선택 시**: 세무서가 신규 점포 주소 반영된 최신 등기부등본을 요구할 가능성 높음 → 지점 등기를 E 사업자등록 선행으로 진행 권장.',
        '**별도 사업자등록 선택 시**: 본점 등기부만으로 사업자등록 가능, 지점 등기는 병행 또는 후행 가능.',
        '기간 도과 시 과태료 (상법 §635) — 미등기 1주 초과부터 발생, 최대 500만원',
        '법인 1호점이 본점 소재지와 같으면 지점 등기 불필요. 2호점부터 적용',
      ],
    },
  },
  {
    id: 'F',
    label: 'F',
    name: '통신판매업 신고',
    org: '구청 (정부24)',
    duration: '즉시~2일',
    docs: ['E 사업자등록증', '구매안전서비스 이용 확인증'],
    note: '배달앱·온라인 운영 시 필수. 법인등기사항증명서 추가.',
    category: 'core',
    description: '배달앱 또는 자체 온라인 주문이 있으면 필요합니다.',
    franchiseOnly: false,
    detail: {
      agency: '관할 시·군·구청 (정부24 온라인 신고 가능)',
      applicationMethod: '정부24 온라인 / 방문 신고',
      processingTime: '즉시~2일 (업무일 기준)',
      fee: '없음',
      documents: [
        { name: '통신판매업 신고서', required: '필수' },
        { name: '사업자등록증 사본 (E)', required: '필수' },
        { name: '법인등기사항증명서', required: '필수(법인)', condition: '행정정보공동이용 동의 시 공무원 직접 조회로 대체 가능' },
        { name: '구매안전서비스 이용확인증', required: '조건부', condition: '선지급식 통신판매(자사 홈페이지·앱 직접 결제) 시 필수 — PG사 또는 은행 에스크로 가입 후 발급 (1~3 영업일)' },
      ],
      prerequisites: [
        {
          what: '구매안전서비스 이용확인증',
          detail: '선지급식(자사 결제 시) PG사 가입 완료 후 발급. 배달앱 입점만이면 면제 가능. PRE-PG(PG 계약) 단계 참조.',
        },
      ],
      references: [
        { label: '통신판매업 신고 (정부24)', url: 'https://www.gov.kr/mw/AA020InfoCappView.do?CappBizCD=11300000006' },
        { label: '전자상거래법 §13', url: 'https://www.law.go.kr/lsInfoP.do?lsId=001753' },
      ],
      notes: [
        '배달앱(배민/쿠팡이츠/요기요) 입점만 하는 경우 — 배달앱이 자체 구매안전 운영 → 별도 PG 가입 면제 가능',
        '자사 홈페이지·앱에서 직접 결제받는 경우 = PG 계약 및 구매안전서비스 이용확인증 필수',
        '법인 본점에서 1건만 신고. 지점별 별도 신고 불필요.',
      ],
    },
  },
];

/** 추가 인허가 7종 (addon) */
const ADDON_STEPS: Step[] = [
  {
    id: '4.1',
    label: '4.1',
    name: '주류면허 (의제주류판매업)',
    org: '관할 세무서',
    duration: '영업 개시일 +30일 이내',
    docs: ['영업신고증 (D)', '신분증'],
    note: '일반음식점에서 주류 판매 시 의무. 휴게음식점은 주류 판매 자체 불가.',
    category: 'addon',
    description: '일반음식점 + 주류 판매 선택 시 자동 표시됩니다.',
    franchiseOnly: false,
  },
  {
    id: '4.2',
    label: '4.2',
    name: '정화조 (오수처리시설) 신고',
    org: '시군구청',
    duration: '착공 전',
    docs: ['오수처리시설 설계도', '건축허가서 사본'],
    note: '공공 하수도 연결 지역은 배수설비로 대체. 기존 건물 임차+기존 정화조는 면제.',
    category: 'addon',
    description: '신축·증축·용도변경 입점 시 필요합니다.',
    franchiseOnly: false,
  },
  {
    id: '4.3',
    label: '4.3',
    name: 'LPG 가스 사용시설 완성검사',
    org: '한국가스안전공사 / 시군구청',
    duration: '설치 후 신청',
    docs: ['LPG 시설 설계도면', '설치 확인서'],
    note: '식품접객업은 사용량 무관 특정사용자로 분류. 도시가스 사용 시 면제.',
    category: 'addon',
    description: 'LPG 용기(도시가스 아닌 경우) 사용 시 필요합니다.',
    franchiseOnly: false,
  },
  {
    id: '4.4',
    label: '4.4',
    name: '옥외광고물 신고',
    org: '시군구청',
    duration: '설치 전',
    docs: ['광고물 도면 (크기·재료·색상)', '건물주 동의서'],
    note: '1~3층 + 5㎡ 이하 가로형 1개는 자유표시(신고 불필요). 조례별 상이.',
    category: 'addon',
    description: '간판 5㎡ 초과 또는 돌출간판 설치 시 신고 필요.',
    franchiseOnly: false,
  },
  {
    id: '4.5',
    label: '4.5',
    name: '외국인 고용 신고',
    org: '고용노동부 고용센터',
    duration: '고용 전',
    docs: ['고용허가신청서', '외국인 여권 사본'],
    note: 'F-2/F-4/F-5/F-6 비자는 신고 불필요. E-9·H-2만 해당.',
    category: 'addon',
    description: 'E-9(비전문취업)·H-2(방문취업 동포) 비자 직원 고용 시 필요.',
    franchiseOnly: false,
  },
  {
    id: '4.6',
    label: '4.6',
    name: '심야영업 참고 (학교 근처)',
    org: '교육청 / 구청',
    duration: '운영 전 확인',
    docs: ['위치 지도 (학교 반경 50m 여부 확인)'],
    note: '일반음식점 영업시간 제한 없음. 단, 학교환경위생정화구역(50m 이내)은 심야 제한 가능.',
    category: 'addon',
    description: '학교 50m 이내 매장은 심야영업 제한이 있는지 확인하세요.',
    franchiseOnly: false,
  },
  {
    id: '4.7',
    label: '4.7',
    name: '화재배상책임보험 가입',
    org: '관할 소방서',
    duration: '영업 개시 전',
    docs: ['보험 가입 증명서'],
    note: '다중이용업소 해당 시 의무 (소방완비 동일 조건). 인명 1인 1억5천만원/사고당 재산 10억원.',
    category: 'addon',
    description: '소방완비증명서(C) 필요 조건과 동일 — 함께 표시됩니다.',
    franchiseOnly: false,
  },
  {
    id: 'PRE-PG',
    label: 'PG',
    number: 'PG',
    name: 'PG 계약 / 구매안전서비스 가입',
    org: 'PG사 (KG이니시스·NHN KCP·토스페이먼츠 등) 또는 은행 에스크로',
    duration: '심사 1~3주 (PG사별 상이)',
    predecessor: '—',
    docs: ['사업자등록증 사본', '대표자 신분증', '통장 사본'],
    note: '자사 온라인 주문 시 통신판매업 신고(F)의 선행. 배달앱 입점만이면 면제 가능.',
    category: 'addon',
    description: '자사 홈페이지·앱에서 직접 결제받는 경우 필요합니다.',
    franchiseOnly: false,
    detail: {
      agency: 'PG사 (KG이니시스·NHN KCP·토스페이먼츠 등) 또는 은행 에스크로 서비스',
      applicationMethod: 'PG사 홈페이지 가입 → 사업자 등록 후 정식 계약',
      processingTime: '심사 1~3주 (PG사별 상이)',
      fee: '월 기본료 + 거래 수수료 (PG사별·업종별 상이)',
      documents: [
        { name: '사업자등록증 사본', required: '필수' },
        { name: '법인등기부등본', required: '필수(법인)' },
        { name: '대표자 신분증', required: '필수' },
        { name: '통장 사본', required: '필수' },
      ],
      references: [
        { label: '전자상거래법 §13 (선지급식 구매안전서비스)', url: 'https://www.law.go.kr/lsInfoP.do?lsId=001753' },
      ],
      notes: [
        '통신판매업 신고 시 "선지급식 통신판매" = 결제 후 배송/제공인 경우 구매안전서비스 이용확인증 의무 첨부',
        '배달앱(배민/쿠팡이츠/요기요) 입점만 하는 경우 = 배달앱이 자체 구매안전 운영 → 자체 PG 가입 면제',
        '자사 홈페이지·앱에서 직접 결제받는 경우 = PG 계약 필수',
      ],
    },
  },
  {
    id: 'PRE-FIRE',
    label: '방염',
    number: '방염',
    name: '방염처리 (방염대상물품)',
    org: '방염업체 (한국소방산업기술원 인증)',
    duration: '1~2주 (방염처리 + 성능시험)',
    predecessor: 'C(소방완비) 선행',
    docs: ['방염처리 계약서', '방염성능시험 합격증'],
    note: '목재·커튼·카펫 등 방염 대상물품이 있으면 소방완비증명(C)의 선행 절차.',
    category: 'addon',
    description: '방염 대상물품(목재·커튼·카펫 등)이 있으면 소방완비 전에 완료해야 합니다.',
    franchiseOnly: false,
    detail: {
      agency: '한국소방산업기술원 인증 방염업체',
      applicationMethod: '방염업체 계약 → 방염처리 → 소방서 성능시험 신청 (완공신고 전)',
      processingTime: '1~2주 (방염업체 일정 + 성능시험 3~5일)',
      fee: '면적·자재별 상이 (수십~수백만원)',
      documents: [
        { name: '방염처리 계약서', required: '필수' },
        { name: '방염성능시험 합격증', required: '필수', condition: '소방서 또는 한국소방산업기술원 발급' },
      ],
      references: [
        { label: '소방시설 설치 및 관리에 관한 법률 §20', url: 'https://www.law.go.kr/lsInfoP.do?lsId=015456' },
      ],
      notes: [
        '방염대상물품 = 커튼·카펫·합판·목재 일부 (소방시설법 시행령 별표 4)',
        '소방완비증명 받기 전 방염성능시험 합격증이 완공신고 첨부서류에 포함됨',
        '방염 처리 안 한 자재 사용 시 소방완비증명 발급 불가',
      ],
    },
  },
];

export const STEPS: Step[] = [...CORE_STEPS, ...ADDON_STEPS];
export const CORE_STEPS_LIST: Step[] = CORE_STEPS;
export const ADDON_STEPS_LIST: Step[] = ADDON_STEPS;

/** 화살표 레이아웃을 위한 흐름 정의 (v1 호환) */
export const FLOW_ROWS: Array<'single' | 'parallel'> = [
  'single',   // P-2
  'single',   // P-3
  'single',   // P-1
  'parallel', // A · B · C
  'single',   // D
  'single',   // E
  'single',   // F
];
